import React from 'react';

/**
 * The onboarding steps all ask the same shape of question: a heading, then a row of
 * pickable cards (Yes / No, or a short list of choices).
 *
 * Each step used to hand-write those cards with `px-16 py-10` and `space-x-4` on a
 * non-wrapping flex row, which ran off the side of a phone. These two components
 * hold the presentation in one place; every step keeps its own `onClick` and its own
 * selected check, so no step's behaviour moves in here.
 */

export const OptionCardRow = ({ children, className = '' }) => (
    <div className={`mt-8 flex w-full flex-col items-stretch justify-center gap-3 sm:mt-14 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4 ${className}`}>
        {children}
    </div>
);

/** The "OR" that sits between a Yes and a No card. */
export const OptionCardSeparator = ({ children = 'OR' }) => (
    <div className="text-center text-sm font-semibold uppercase tracking-wide text-slate-400">
        {children}
    </div>
);

export const OptionCard = ({ selected, onClick, children, icon = null, className = '' }) => (
    <button
        type="button"
        onClick={onClick}
        className={`relative flex min-h-[60px] w-full flex-col items-center justify-center gap-2 rounded-2xl border px-6 py-4 text-center transition-all sm:min-h-[120px] sm:w-auto sm:min-w-[11rem] sm:px-12 ${
            selected
                ? 'border-[#fb6635] bg-orange-50 text-[#211b4f] ring-1 ring-[#fb6635]'
                : 'border-slate-200 bg-white text-slate-600 shadow-sm hover:border-orange-200 hover:bg-orange-50/40'
        } ${className}`}
    >
        {icon}
        <span className="text-base font-semibold sm:text-lg">{children}</span>
        {selected && (
            <span className="absolute right-2 top-2 text-[#fb6635]">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" className="bi bi-check-circle" viewBox="0 0 16 16">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16"/>
                    <path d="m10.97 4.97-.02.022-3.473 4.425-2.093-2.094a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05"/>
                </svg>
            </span>
        )}
    </button>
);

/** Shared page heading for a step. */
export const StepHeading = ({ children, className = '' }) => (
    <h3 className={`text-center text-2xl font-bold tracking-tight text-[#0a1445] sm:text-3xl ${className}`}>
        {children}
    </h3>
);

/** Shared wrapper that centres a step's content and keeps phone gutters sane. */
export const StepShell = ({ children, className = '' }) => (
    <div className={`mt-4 flex flex-col items-center justify-center px-1 sm:mt-10 ${className}`}>
        {children}
    </div>
);
